import { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import SignatureCanvas from "react-signature-canvas";
import swal from "sweetalert";
import api from "../../../api";

const SignatureModal = ({ show, onHide, onSave, title = "Capture Signature", showStampUpload = false }) => {
  const dispatch = useDispatch();
  const sigPadRef = useRef(null);
  const [stampFile, setStampFile] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [signMode, setSignMode] = useState("profile");
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const userDetails = useSelector((state) => state.auth?.userDetails);
  const [hasProfileSignature, setHasProfileSignature] = useState(Boolean(userDetails?.signature));

  const getProfileSignatureUrl = (sig) => {
    const signature = sig || userDetails?.signature;
    if (!signature) return null;
    if (signature.startsWith("blob:")) return signature;
    if (/^https?:\/\//i.test(signature)) return signature;
    const base = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/$/, "");
    const cleanPath = signature.startsWith("/") ? signature : `/${signature}`;
    return `${base}${cleanPath}`;
  };

  const createFileFromSignatureUrl = async () => {
    // Always use the backend proxy — handles both local and S3, returns correct image/png
    try {
      const response = await api.get("/users/my-signature", { responseType: "blob" });
      const blob = response.data;
      const file = new File([blob], "signature.png", { type: blob.type || "image/png" });
      return file;
    } catch (error) {
      if (profilePreviewUrl && !profilePreviewUrl.startsWith("blob:")) {
        try {
          const res = await fetch(profilePreviewUrl);
          const blob = await res.blob();
          return new File([blob], "signature.png", { type: blob.type || "image/png" });
        } catch (fetchErr) {
          // ignore
        }
      }
      throw new Error("Could not load profile signature from server. Please draw your signature instead.");
    }
  };

  // Sync state if userDetails?.signature changes
  useEffect(() => {
    if (userDetails?.signature) {
      setHasProfileSignature(true);
    }
  }, [userDetails?.signature]);

  // When modal opens, pre-load profile signature
  useEffect(() => {
    let isMounted = true;
    let objectUrlToRevoke = null;

    if (show) {
      if (userDetails?.signature) {
        setHasProfileSignature(true);
        setSignMode("profile");
        const signatureUrl = getProfileSignatureUrl(userDetails.signature);
        setProfilePreviewUrl(signatureUrl);
        setProfileLoading(false);
      } else {
        // Fallback: If not in redux (e.g. after switching user), dynamically check backend
        setProfileLoading(true);
        api.get("/users/my-signature", { responseType: "blob" })
          .then((response) => {
            if (!isMounted) return;
            const blob = response.data;
            if (blob && blob.size > 0) {
              const objUrl = URL.createObjectURL(blob);
              objectUrlToRevoke = objUrl;
              setProfilePreviewUrl(objUrl);
              setHasProfileSignature(true);
              setSignMode("profile");

              // Sync Redux & localStorage so future opens are instant
              dispatch({
                type: "UPDATE_USER_DETAILS",
                payload: { signature: "/api/users/my-signature" },
              });
            } else {
              setHasProfileSignature(false);
              setSignMode("draw");
            }
          })
          .catch(() => {
            if (!isMounted) return;
            setHasProfileSignature(false);
            setSignMode("draw");
          })
          .finally(() => {
            if (isMounted) setProfileLoading(false);
          });
      }
    } else {
      setStampFile(null);
      setRemarks("");
      setProfilePreviewUrl(null);
      setProfileLoading(false);
      if (sigPadRef.current) sigPadRef.current.clear();
    }

    return () => {
      isMounted = false;
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [show, userDetails?.signature, dispatch]);

  const clearCanvas = () => {
    if (sigPadRef.current) sigPadRef.current.clear();
    setStampFile(null);
  };

  // Convert base64 dataURL → File
  const dataUrlToFile = (dataUrl, filename = "signature.png") => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([new Blob([u8arr], { type: mime })], filename, { type: mime });
  };

  const handleSave = async () => {
    if (signMode === "profile") {
      if (!profilePreviewUrl) {
        return swal("Error", "Profile signature could not be loaded. Please draw your signature instead.", "error");
      }
      try {
        const file = await createFileFromSignatureUrl();
        onSave(file, stampFile, remarks.trim());
      } catch (error) {
        swal("Error", error.message || "Could not prepare profile signature.", "error");
      }
    } else {
      if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
        return swal("Error", "Please sign before saving", "error");
      }
      const dataURL = sigPadRef.current.toDataURL("image/png");
      const file = dataUrlToFile(dataURL, "signature.png");
      onSave(file, stampFile, remarks.trim());
      clearCanvas();
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Signature Type Radio */}
        <div className="mb-3">
          <Form.Label className="font-w600 d-block mb-2">Signature Type</Form.Label>
          <div className="d-flex gap-4">
            <Form.Check
              type="radio"
              id="sig-profile"
              name="signMode"
              label="Use Profile Signature"
              value="profile"
              checked={signMode === "profile"}
              onChange={() => setSignMode("profile")}
              disabled={!hasProfileSignature && !profileLoading}
            />
            <Form.Check
              type="radio"
              id="sig-draw"
              name="signMode"
              label="Draw Signature"
              value="draw"
              checked={signMode === "draw"}
              onChange={() => setSignMode("draw")}
            />
          </div>
        </div>

        {/* Profile Signature Preview */}
        {signMode === "profile" && (
          <div className="mb-3">
            {profileLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                <span className="text-muted small">Loading signature...</span>
              </div>
            ) : !hasProfileSignature ? (
              <div className="alert alert-warning mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                No profile signature uploaded.{" "}
                <a href="/profile" target="_blank" rel="noreferrer">Go to Profile Settings</a>
                {" "}to upload one, or select <strong>Draw Signature</strong> above.
              </div>
            ) : profilePreviewUrl ? (
              <div className="text-center">
                <div className="border rounded p-3 bg-light d-inline-block" style={{ minWidth: "280px" }}>
                  <img
                    src={profilePreviewUrl}
                    alt="Profile Signature"
                    style={{ maxWidth: "300px", maxHeight: "110px", objectFit: "contain" }}
                    onError={async (e) => {
                      // Fallback: fetch blob from backend proxy if direct url fails to render
                      try {
                        const res = await api.get("/users/my-signature", { responseType: "blob" });
                        const objUrl = URL.createObjectURL(res.data);
                        setProfilePreviewUrl(objUrl);
                      } catch (err) {
                        e.target.style.display = "none";
                      }
                    }}
                  />
                </div>
                <p className="text-muted small mt-1 mb-0">Your saved profile signature</p>
              </div>
            ) : (
              <div className="alert alert-danger mb-0">
                <i className="fas fa-times-circle me-2"></i>
                Could not load profile signature.{" "}
                <button className="btn btn-link p-0" onClick={() => setSignMode("draw")}>
                  Draw instead
                </button>
              </div>
            )}
          </div>
        )}

        {/* Draw Canvas */}
        {signMode === "draw" && (
          <div className="mb-3">
            <div style={{ border: "1px solid #ccc", background: "#f9f9f9", borderRadius: "4px" }}>
              <SignatureCanvas
                ref={sigPadRef}
                penColor="black"
                canvasProps={{ width: 465, height: 180, className: "sigCanvas" }}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1">
              <small className="text-muted">Sign inside the box above.</small>
              <Button variant="link" size="sm" className="p-0 text-secondary" onClick={clearCanvas}>
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* Remarks */}
        <Form.Group className="mb-3">
          <Form.Label className="font-w600">Remarks</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Enter remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </Form.Group>

        {/* Stamp Upload */}
        {showStampUpload && (
          <Form.Group>
            <Form.Label className="font-w600">Upload Stamp (Optional)</Form.Label>
            <Form.Control
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setStampFile(e.target.files[0])}
            />
          </Form.Group>
        )}

      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={
            profileLoading ||
            (signMode === "profile" && !profilePreviewUrl)
          }
        >
          {profileLoading ? "Loading..." : "Save & Approve"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SignatureModal;